import { FC, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { Modal, SnackbarAlert } from "@/core/components"
import { formatDateAndTime, getApiErrorMessage } from "@/core/utils"
import { GuestBot } from "../../interfaces"
import { useCancelMailingMutation, useGetMailingQuery } from "../../store"
import { useGuestBotWriteAccess } from "../../hooks"
import {
  CHATS_POLL_MS,
  MAILING_CHAT_STATUS_COLOR,
  MAILING_CHAT_STATUS_LABEL,
  MAILING_STATUS_COLOR,
  MAILING_STATUS_LABEL,
  isMailingTerminal,
} from "../../utils/chatStatus"

const CHAT_STATUS_ORDER: GuestBot.MailingChatStatus[] = [
  "pending",
  "sent",
  "deleted",
  "failed",
  "cancelled",
]
const ALWAYS_VISIBLE: GuestBot.MailingChatStatus[] = ["sent", "deleted"]

interface IProps {
  botId: string
  mailingId: string
}

export const MailingProgress: FC<IProps> = ({ botId, mailingId }) => {
  const canWrite = useGuestBotWriteAccess()
  const navigate = useNavigate()
  const [pollingInterval, setPollingInterval] = useState(CHATS_POLL_MS)
  const { data, isFetching, isError, error } = useGetMailingQuery(
    { pathParams: { id: botId, mailingId } },
    { pollingInterval },
  )
  const [cancelMailing, { isLoading: isCancelling }] =
    useCancelMailingMutation()
  const [cancelOpen, setCancelOpen] = useState(false)
  const [snackbar, setSnackbar] = useState<{
    variant: "success" | "error"
    message: string
  } | null>(null)

  // Poll only while non-terminal; stop on query error too (spec §7).
  useEffect(() => {
    if (isError) {
      setPollingInterval(0)
      return
    }
    if (data && isMailingTerminal(data.status)) setPollingInterval(0)
  }, [data, isError])

  const counts = useMemo(() => {
    const result = new Map<GuestBot.MailingChatStatus, number>()
    ;(data?.items ?? []).forEach((c) =>
      result.set(c.status, (result.get(c.status) ?? 0) + 1),
    )
    return result
  }, [data])

  const chats = useMemo(
    () => [...(data?.items ?? [])].sort((a, b) => a.chatId - b.chatId),
    [data],
  )

  const goBack = () => navigate("/telegram/guest-bots/chats")

  if (isError)
    return (
      <Stack spacing={2}>
        <Alert severity="error">{getApiErrorMessage(error)}</Alert>
        <Button onClick={goBack}>Назад к списку</Button>
      </Stack>
    )
  if (!data) return isFetching ? <CircularProgress size={24} /> : null

  const isTerminal = isMailingTerminal(data.status)

  const handleCancel = async () => {
    try {
      await cancelMailing({
        pathParams: { id: botId, mailingId },
      }).unwrap()
      setSnackbar({ variant: "success", message: "Рассылка отменена" })
    } catch (e) {
      setSnackbar({ variant: "error", message: getApiErrorMessage(e) })
    } finally {
      setCancelOpen(false)
    }
  }

  return (
    <Stack spacing={2}>
      <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
        <Chip
          size="small"
          label={MAILING_STATUS_LABEL[data.status]}
          color={MAILING_STATUS_COLOR[data.status]}
        />
        {CHAT_STATUS_ORDER.filter(
          (s) => ALWAYS_VISIBLE.includes(s) || (counts.get(s) ?? 0) > 0,
        ).map((s) => (
          <Chip
            key={s}
            size="small"
            variant="outlined"
            label={`${MAILING_CHAT_STATUS_LABEL[s]}: ${counts.get(s) ?? 0}`}
          />
        ))}
      </Box>

      {/* D-20: the only posted-text surface — read from the detail (mailing.text
          = resolved trigger base; snapshot username). Empty text → @username only. */}
      <Typography variant="body2">
        {`Опубликовано: ${data.text ? `${data.text} ` : ""}@${data.botUsernameSnapshot ?? "—"}`}
      </Typography>

      {data.status === "failed" && data.lastError && (
        <Alert severity="error">{data.lastError}</Alert>
      )}

      {canWrite && (
        <Box>
          <Button
            variant="outlined"
            color="error"
            disabled={isTerminal}
            onClick={() => setCancelOpen(true)}
          >
            Отменить рассылку
          </Button>
        </Box>
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Чат</TableCell>
            <TableCell>Статус</TableCell>
            <TableCell>Причина</TableCell>
            <TableCell>Отправлено</TableCell>
            <TableCell>Удалено</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {chats.map((c) => (
            <TableRow key={c.id}>
              <TableCell>{c.chatTitle ?? `Чат ${c.chatId}`}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={MAILING_CHAT_STATUS_LABEL[c.status]}
                  color={MAILING_CHAT_STATUS_COLOR[c.status]}
                />
              </TableCell>
              <TableCell>{c.reason ?? "—"}</TableCell>
              <TableCell>
                {c.sentAt ? formatDateAndTime(c.sentAt) : "—"}
              </TableCell>
              <TableCell>
                {c.deletedAt ? formatDateAndTime(c.deletedAt) : "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box display="flex" gap={1}>
        {/* Read affordance — not write-gated; tied to [D3-d] `mailing:<id>` labels (AC 17). */}
        <Button
          onClick={() =>
            navigate(
              `/telegram/guest-bots/logs?botId=${botId}&source=mailing:${mailingId}`,
            )
          }
        >
          Ответы бота в чатах рассылки
        </Button>
        <Button onClick={goBack}>Назад к списку</Button>
      </Box>

      {cancelOpen && (
        <Modal
          disableBackdropClose
          title="Отменить рассылку?"
          onClose={() => setCancelOpen(false)}
          element={
            <Box>
              <Typography>
                Ожидающие чаты будут отменены. Уже отправленные сообщения не
                изменятся.
              </Typography>
              <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
                <Button onClick={() => setCancelOpen(false)}>
                  Не отменять
                </Button>
                <Button
                  color="error"
                  variant="contained"
                  disabled={isCancelling}
                  onClick={handleCancel}
                >
                  Отменить рассылку
                </Button>
              </Box>
            </Box>
          }
        />
      )}
      <SnackbarAlert
        isOpen={!!snackbar}
        variant={snackbar?.variant}
        message={snackbar?.message ?? ""}
        onClose={() => setSnackbar(null)}
      />
    </Stack>
  )
}
